import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { convertsCurrencyToCents, debounce } from '@npi/buddy-utils';

import { HandleValueInCentsCallback } from './useConvertCurrencyToCentsDebounced.types';
import useHandleMinMax from './useHandleMinMax';

export type UseConvertCurrencyToCentsProps = {
    amountInCurrency: number;
    handleValue?: HandleValueInCentsCallback;
    debounceDurationInMs?: number;
    min?: number;
    max?: number;
    errorMessage?: string;
    setErrorMessage?: Dispatch<SetStateAction<string>>;
    touched?: boolean;
};

export function useConvertCurrencyToCentsDebounced({
    amountInCurrency,
    handleValue,
    debounceDurationInMs = 500,
    min,
    max,
    errorMessage,
    setErrorMessage,
    touched,
}: UseConvertCurrencyToCentsProps) {
    const [amountInCents, setAmountInCents] = useState(0);

    useHandleMinMax({
        min,
        max,
        errorMessage,
        setErrorMessage,
        touched,
        amountInCents,
        amountInCurrency,
        handleValue,
    });

    useEffect(() => {
        if (handleValue && Math.round(amountInCents) === Math.round(amountInCurrency * 100)) {
            handleValue(amountInCents, amountInCurrency);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountInCents, handleValue]);

    useEffect(() => {
        const handleCurrencyToCents = (amountInCurrency: number) => {
            setAmountInCents(convertsCurrencyToCents(amountInCurrency));
        };

        const debouncedOnChange = debounce(() => {
            return handleCurrencyToCents(amountInCurrency);
        }, debounceDurationInMs);
        debouncedOnChange();

        return () => {
            debouncedOnChange.cancel();
        };
    }, [amountInCurrency, debounceDurationInMs]);

    return { amountInCents };
}

export default useConvertCurrencyToCentsDebounced;
